import { useEffect, useRef, useMemo } from 'preact/hooks';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import '../../styles/photoswipe-custom.css';

// Global singleton instance
let globalLightbox = null;
let initCount = 0;

/** Target width for lightbox/zoom — large enough for zoom to be useful (Shopify max 5760) */
const LIGHTBOX_IMAGE_WIDTH = 2880;

/**
 * Convert a Shopify CDN image URL to request a larger size so zoom has room.
 * Returns { src, width, height } for the high-res slide.
 */
const getHighResImageUrl = (
  url,
  origWidth,
  origHeight,
  targetWidth = LIGHTBOX_IMAGE_WIDTH,
) => {
  if (!url || typeof url !== 'string') {
    return { src: url, width: origWidth, height: origHeight };
  }
  const w = Math.max(1, Number(origWidth) || 1200);
  const h = Math.max(1, Number(origHeight) || 800);
  const targetW = Math.min(targetWidth, 5760);
  const targetH = Math.round((targetW * h) / w);

  let newUrl = url;
  try {
    const fullUrl = url.startsWith('http') ? url : `https:${url}`;
    const parsed = new URL(fullUrl, 'https://cdn.shopify.com');
    if (parsed.hostname.includes('shopify')) {
      parsed.searchParams.set('width', String(targetW));
      parsed.searchParams.set('height', String(targetH));
      newUrl = parsed.toString();
      if (url.startsWith('//')) newUrl = newUrl.replace(/^https?:/, '');
    }
  } catch {
    // Keep original URL; still report target dimensions so zoom has headroom
  }
  return { src: newUrl, width: targetW, height: targetH };
};

/**
 * Build PhotoSwipe dataSource array from product media (images only).
 * Uses high-res image URLs so zoom in the lightbox works well.
 */
const buildDataSourceFromMedia = (media) => {
  if (!media?.length) return [];
  return media
    .filter((item) => item.mediaContentType === 'IMAGE')
    .map((item) => {
      const sources = item.sources || [];
      const largest = [...sources]
        .sort((a, b) => (b.width || 0) - (a.width || 0))[0];
      const origWidth = largest?.width || item.previewImage?.width || 1200;
      const origHeight = largest?.height || item.previewImage?.height || 800;
      const origSrc = largest?.url
        || item.previewImage?.url
        || item.previewImage?.src;
      const alt = item.previewImage?.alt || sources[0]?.alt || '';
      const { src, width, height } = getHighResImageUrl(
        origSrc,
        origWidth,
        origHeight,
      );
      return { src, width, height, alt };
    })
    .filter((slide) => slide.src);
};

const usePhotoSwipe = (gallerySelector, media) => {
  const lightboxRef = useRef(null);

  const dataSource = useMemo(() => buildDataSourceFromMedia(media), [media]);
  const useDataSource = dataSource.length > 0;

  useEffect(() => {
    initCount += 1;

    const options = {
      pswpModule: () => import('photoswipe'),
      bgOpacity: 0.75,
    };

    if (useDataSource) {
      options.dataSource = dataSource;
    } else {
      options.gallery = gallerySelector;
      options.children = 'a';
    }

    if (globalLightbox) {
      globalLightbox.destroy();
      globalLightbox = null;
    }

    globalLightbox = new PhotoSwipeLightbox(options);
    globalLightbox.on('close', () => {
      document.body.classList.remove('overflow-hidden');
    });
    globalLightbox.init();
    lightboxRef.current = globalLightbox;

    return () => {
      initCount -= 1;
      if (initCount === 0 && globalLightbox) {
        globalLightbox.destroy();
        globalLightbox = null;
      }
    };
  }, [gallerySelector, media, useDataSource, dataSource]);

  return { lightboxRef, useDataSource };
};

export { buildDataSourceFromMedia };

export default usePhotoSwipe;
